import React, { useEffect, useState } from 'react';
import { CalendarDays, Clock, Sparkles, Users, Video, X } from 'lucide-react';
import { apiService } from '../../services/api';
import TalkingPointsPanel from './TalkingPointsPanel';

// Préparer une réunion de l'agenda : ses points à aborder (ceux du projet, ou du client, et ceux notés pour cette
// réunion récurrente), avec l'aide de Muse. Le projet choisi vaut pour toute la série, et pour tout le monde.
const PrepareMeetingDialog = ({ meeting, onClose, onChanged }) => {
  const [projectId, setProjectId] = useState(meeting.project_id || null);
  const [projectName, setProjectName] = useState(meeting.project_name || null);
  const [clientId, setClientId] = useState(meeting.project_id ? null : meeting.client_id || null);
  const [projects, setProjects] = useState([]);
  const [choosing, setChoosing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!choosing || projects.length) return;
    apiService.get('/projects/options?statuses=in_progress').then(setProjects).catch(() => setProjects([]));
  }, [choosing, projects.length]);

  useEffect(() => {
    const onKey = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const link = async (value) => {
    const id = value === 'none' ? null : Number(value);
    setChoosing(false);
    try {
      if (meeting.series_id) await apiService.put('/home/event-links', { series_id: meeting.series_id, project_id: id });
      setProjectId(id);
      setProjectName(id ? projects.find((p) => p.id === id)?.name : null);
      setClientId(null);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const scope = projectId ? projectName : clientId ? meeting.client_name : 'cette réunion';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 px-4 py-10" onMouseDown={onClose}>
      <div className="w-full max-w-xl rounded-2xl bg-white shadow-xl" onMouseDown={(event) => event.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">Préparer la réunion</p>
            <h2 className="mt-0.5 text-lg font-semibold text-slate-900">{meeting.title}</h2>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
              {meeting.day_label && <span className="flex items-center gap-1"><CalendarDays size={12} /> {meeting.day_label}</span>}
              {meeting.time && (
                <span className="flex items-center gap-1"><Clock size={12} /> {meeting.time}{meeting.end_time ? `–${meeting.end_time}` : ''}</span>
              )}
              {meeting.external?.length > 0 && (
                <span className="flex items-center gap-1"><Users size={12} /> avec {meeting.external.join(', ')}</span>
              )}
              {meeting.link && (
                <a href={meeting.link} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-indigo-600 hover:underline">
                  <Video size={12} /> Lien de la visio
                </a>
              )}
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600" title="Fermer">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          {meeting.note && (
            <div className="flex gap-2 rounded-xl bg-indigo-50/70 px-3 py-2.5 text-sm text-slate-700">
              <Sparkles size={15} className="mt-0.5 shrink-0 text-indigo-500" />
              <p>{meeting.note}</p>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-slate-500">Points à aborder pour</span>
            {choosing ? (
              <select autoFocus defaultValue="" onChange={(event) => event.target.value && link(event.target.value)}
                className="rounded-lg border border-slate-200 px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-indigo-100">
                <option value="">Choisir un projet…</option>
                <option value="none">Aucun projet : cette réunion seulement</option>
                {projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
              </select>
            ) : (
              <>
                <span className="rounded-full bg-slate-100 px-2.5 py-0.5 font-medium text-slate-700">{scope}</span>
                {meeting.client_name && projectId && <span className="text-xs text-slate-400">client {meeting.client_name}</span>}
                {meeting.series_id && (
                  <button type="button" onClick={() => setChoosing(true)} className="text-xs text-indigo-600 hover:underline">
                    {projectId ? 'changer' : 'rattacher à un projet'}
                  </button>
                )}
              </>
            )}
          </div>
          {error && <p className="text-xs text-rose-600">{error}</p>}

          <TalkingPointsPanel
            key={`${projectId}-${clientId}`}
            projectId={projectId}
            clientId={projectId ? null : clientId}
            seriesId={meeting.series_id}
            meetingTitle={meeting.title}
            meetingRef={`calendar:${meeting.event_id}`}
            external={(meeting.external || []).length > 0}
            onChange={onChanged}
            emptyHint="Choisis le projet de cette réunion pour préparer ses points."
          />
          <p className="text-[11px] text-slate-400">
            {projectId || clientId
              ? "Les points publics sont partagés avec l'équipe : ceux qui ne sont pas abordés restent pour la prochaine réunion."
              : 'Ces points suivent cette réunion récurrente : ceux qui ne sont pas abordés restent pour la prochaine.'}
            {' '}Un point privé (cadenas) n'est visible que de toi ; un clic le rend public.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrepareMeetingDialog;
